import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Zap, Lock, Package, KeyRound, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCategories } from "@/lib/useCategories";
import { ProductCard } from "@/components/ProductCard";
import { ReviewCarousel } from "@/components/ReviewCarousel";
import { ExplainerVideo } from "@/components/ExplainerVideo";

const HERO_VIDEO = "/images/video.mp4";
const HERO_POSTER = "/images/video-thumb.png";
const PSYDUCK = "/images/psyduck.jpg";
const MINT = "#00E5C0";

export default function Home() {
  const [products, setProducts] = useState([]);
  const { categories } = useCategories();
  const order = categories.map((c) => c.key);

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]));
  }, []);

  // Vault featured: never mix coming-soon into this grid (those belong in Hunting Fleet only).
  const featured = products
    .filter((p) => p.is_featured && !p.coming_soon)
    .sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category));
  const hasAnyFeatured = featured.length > 0;

  // Hunting Fleet strip: unique coming-soon products only (dedupe by id).
  const shundoSoon = (() => {
    const seen = new Set();
    const out = [];
    for (const p of products) {
      if (!p?.coming_soon || !p?.id || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
    return out;
  })();

  return (
    <div data-testid="home-page" className="bg-[#050606]">
      {/* HERO — bg video #2 muted loop; player #3 same file */}
      <section data-testid="home-hero" className="home-hero scanlines relative overflow-hidden border-b border-[#00E5C0]/15">
        <video
          data-testid="hero-video"
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050606] via-[#050606]/90 to-[#050606]/40" />
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-8 px-5 pb-16 pt-[max(5.5rem,calc(env(safe-area-inset-top)+4.5rem))] sm:gap-10 sm:py-24 lg:grid-cols-12 lg:gap-12 lg:px-10 lg:py-36">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="min-w-0 lg:col-span-7"
            data-testid="hero-copy"
          >
            <p
              className="mb-6 text-xs font-bold uppercase tracking-[0.35em]"
              style={{ color: MINT }}
            >
              // vault access
            </p>
            <h1 className="font-display text-4xl font-black leading-none tracking-tighter sm:text-5xl lg:text-6xl">
              POKÉCOINS.
              <br />
              EVENT PASSES.
              <br />
              <span className="neon-text" style={{ color: MINT }}>
                DELIVERED.
              </span>
            </h1>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-zinc-400">
              Event passes and coin drops, sealed and fulfilled. Same stock you know — quieter shell,
              mint vault frame, delivery you can track.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#store"
                data-testid="hero-shop-btn"
                className="hero-cta border hover:bg-[#00E5C0] hover:text-black"
                style={{ borderColor: MINT, color: MINT }}
              >
                View the Vault
              </a>
              <a
                href="#delivery"
                data-testid="hero-delivery-btn"
                className="hero-cta border border-zinc-700 text-zinc-300 hover:border-[#00E5C0] hover:text-[#00E5C0]"
              >
                How Delivery Works
              </a>
            </div>
          </motion.div>

          <div className="w-full lg:col-span-5 lg:flex lg:items-center lg:justify-end">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="explainer-video-frame mx-auto w-full min-w-0 lg:mx-0"
            >
              <ExplainerVideo src={HERO_VIDEO} poster={HERO_POSTER} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST ROW */}
      <section className="border-b border-[#00E5C0]/10 bg-[#0B0D0C]">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-5 py-12 sm:grid-cols-3 lg:px-10">
          {[
            {
              icon: Lock,
              title: "AES Encrypted PTC",
              body: "Credentials sealed with Fernet symmetric encryption. Never stored as plain text.",
            },
            {
              icon: Zap,
              title: "Fast Fulfilment",
              body: "Most Pokécoin bundles are processed within the hour of payment clearing.",
            },
            {
              icon: ShieldCheck,
              title: "Stay-Logged-Out Alerts",
              body: "You get pinged the second an operator logs in and again when it's done.",
            },
          ].map((f) => (
            <div
              key={f.title}
              className="border border-[#00E5C0]/12 bg-[#050606] p-6"
            >
              <f.icon className="h-5 w-5" style={{ color: MINT }} />
              <h3 className="mt-4 font-display text-sm uppercase tracking-wide">{f.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-zinc-500">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED — SellAuth cards unchanged fields; quiet Requires a bundle */}
      <section id="store" className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-28">
        {hasAnyFeatured && (
          <>
            <div className="mb-12 flex flex-wrap items-end justify-between gap-4 border-b border-[#00E5C0]/12 pb-6">
              <h2 className="font-display text-2xl tracking-tight sm:text-3xl lg:text-4xl">
                The Vault
              </h2>
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                Requires a bundle
              </span>
            </div>
            <div
              data-testid="home-featured-grid"
              className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {featured.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}

        {!hasAnyFeatured && (
          <p data-testid="home-no-featured" className="text-xs text-zinc-600">
            No featured products right now — browse the full catalog for everything in stock.
          </p>
        )}

        <div className="mt-16 flex justify-center">
          <Link
            to="/products"
            data-testid="home-all-products-btn"
            className="border border-zinc-700 px-8 py-4 text-[11px] uppercase tracking-[0.3em] text-zinc-300 transition-colors hover:border-[#00E5C0] hover:text-[#00E5C0]"
          >
            View full catalog
          </Link>
        </div>
      </section>

      {/* 3 DELIVERY STEPS */}
      <section
        id="delivery"
        data-testid="delivery-steps"
        className="border-y border-[#00E5C0]/10 bg-[#0B0D0C]"
      >
        <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10 lg:py-24">
          <p
            className="text-xs font-bold uppercase tracking-[0.35em]"
            style={{ color: MINT }}
          >
            // fulfilment
          </p>
          <h2 className="mt-4 font-display text-2xl tracking-tight sm:text-3xl">
            How Delivery Works
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Package,
                step: "01",
                title: "Pick from the vault",
                body: "Choose an event pass or coin drop. Passes quietly require a bundle in cart.",
              },
              {
                icon: KeyRound,
                step: "02",
                title: "Checkout sealed",
                body: "Pay through SellAuth. PTC credentials stay encrypted until an operator needs them.",
              },
              {
                icon: CheckCircle2,
                step: "03",
                title: "Delivered to account",
                body: "We fulfil in-game, ping you when logged in, and again when the drop is complete.",
              },
            ].map((s) => (
              <div
                key={s.step}
                className="border border-[#00E5C0]/12 bg-[#050606] p-6"
              >
                <div className="flex items-center justify-between">
                  <s.icon className="h-5 w-5" style={{ color: MINT }} />
                  <span className="font-display text-xs tracking-[0.2em] text-zinc-600">
                    {s.step}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-sm uppercase tracking-wide">{s.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-zinc-500">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HUNTING FLEET + Psyduck / Shundo waitlist */}
      <section
        id="hunting-fleet"
        data-testid="hunting-fleet-section"
        className="relative overflow-hidden border-b border-[#00E5C0]/10 bg-[#050606] py-20 lg:py-28"
      >
        <div className="pointer-events-none absolute inset-0 flex items-center opacity-[0.06]">
          <div className="marquee-track flex whitespace-nowrap">
            {[0, 1].map((k) => (
              <span key={k} className="font-display text-7xl font-black tracking-tighter lg:text-9xl">
                SHUNDO HUNTING · ITOOLS · PGTOOLS · REGIBOT · SHUNGO&nbsp;
              </span>
            ))}
          </div>
        </div>
        <div className="relative mx-auto grid max-w-[1400px] gap-12 px-5 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-6">
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-[#9966cc]">
              // phase two
            </p>
            <h2 className="mt-5 font-display text-2xl tracking-tight sm:text-3xl lg:text-4xl">
              Phase Two — Hunting Fleet
            </h2>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-zinc-400">
              Advanced, targeted Shundo acquisition handled by our dedicated operator fleet. We use a
              private suite of secure simulation tools to hunt down your exact shiny-hundos while
              keeping your account completely safe. Onboarding opens soon.
            </p>
            <div className="mt-8">
              <Link
                to="/waitlist?focus=hunting_fleet"
                data-testid="hunting-fleet-join-btn"
                className="inline-block border border-[#00E5C0] px-5 py-3 text-[10px] uppercase tracking-[0.3em] text-[#00E5C0] transition-colors hover:bg-[#00E5C0] hover:text-black"
              >
                Join Waitlist
              </Link>
            </div>
            {shundoSoon.length > 0 && (
              <div
                data-testid="hunting-fleet-products"
                className="mt-8 grid gap-4 sm:grid-cols-2"
              >
                {shundoSoon.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-6">
            <img
              src={PSYDUCK}
              alt="Psyduck"
              className="border border-[#00E5C0]/15 object-cover sm:col-span-2"
            />
          </div>
        </div>
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <ReviewCarousel />
        </div>
      </section>

      <footer className="mx-auto max-w-[1400px] px-5 py-12 text-[10px] uppercase tracking-[0.25em] text-zinc-600 lg:px-10">
        © 2026 PokeCoins.cc · unofficial fan marketplace · not affiliated with Niantic or The Pokémon
        Company
      </footer>
    </div>
  );
}
